import { apiGet, apiPut } from './client';

/**
 * MKS-471 (Phase 128 / Plan 128-03) — frontend client for the admin card of the per-repository
 * Factory policy (`/api/admin/bot/factory-repo-policies`). Field names mirror
 * `factoryRepoPolicyListItemJsonSchema` (`backend/src/api/schemas/factoryRepoPolicy.ts`) EXACTLY.
 *
 * Phase 130 (Plan 130-15, D-01): the policy is the repository's `.monkfactory/factory.yml` at main,
 * shown read-only through `file`; the only write left is the Paperclip project name.
 */

export type FactoryRepoTaskCodeTitle = 'deploy_marks_done' | 'traceability_only';

/** The gate as `.monkfactory/factory.yml` carries it. */
export interface FactoryRepoFileGate {
  requiredContexts: string[];
  ciConfigPath: string | null;
  ciWorkflow: string | null;
  protectedPrefixes: string[];
  protectedFiles: string[];
  testChangeRule: { sourcePrefix: string; testPatterns: string[] } | null;
  releaseFlagDir: string | null;
  cliBundleRule: boolean;
  taskCodeTitle: FactoryRepoTaskCodeTitle;
}

export type FactoryRepoDeploy =
  | {
      kind: 'ci_status';
      contexts: { production: string; staging: string };
      creditsDoneMark: boolean;
      approvalGate?: string;
    }
  | { kind: 'manual'; creditsDoneMark: boolean };

export type FactoryRepoFileStatus = 'unread' | 'ok' | 'absent' | 'invalid';

/** Phase 130 (D-01) — the cached `.monkfactory/factory.yml` read of one repository. */
export interface FactoryRepoFileInfo {
  status: FactoryRepoFileStatus;
  mainSha: string | null;
  fetchedAt: string | null;
  error: string | null;
  gate: FactoryRepoFileGate | null;
  deploy: FactoryRepoDeploy | null;
  runner: { sidecars: string[] } | null;
  dockerfilePresent: boolean | null;
}

/** Phase 130 (Plan 130-03) — one per-project agent runtime row. Status and shas only. */
export interface FactoryRepoRunner {
  paperclipCompanyId: string;
  status: string;
  desiredSha: string | null;
  builtSha: string | null;
  errorCode: string | null;
  updatedAt: string;
}

export interface FactoryRepoPolicyItem {
  /** The stored name row's id; null when nothing is stored for the repository. */
  id: number | null;
  repository: string;
  /** True when nothing is stored for the repository (no Paperclip project name row). */
  isDefault: boolean;
  allowlisted: boolean;
  paperclipProjectName: string | null;
  updatedAt: string | null;
  updatedBy: { id: number; name: string } | null;
  file: FactoryRepoFileInfo;
  runners: FactoryRepoRunner[];
}

/** The only policy write left: exactly these two keys (any other key is a 400). */
export interface FactoryRepoPolicyInput {
  repository: string;
  paperclipProjectName: string | null;
}

const BASE = '/admin/bot/factory-repo-policies';

export async function getFactoryRepoPolicies(): Promise<FactoryRepoPolicyItem[]> {
  const res = await apiGet<{ data: FactoryRepoPolicyItem[] }>(BASE);
  return res.data;
}

export async function upsertFactoryRepoPolicy(
  input: FactoryRepoPolicyInput
): Promise<FactoryRepoPolicyItem> {
  const res = await apiPut<{ data: FactoryRepoPolicyItem }>(BASE, input);
  return res.data;
}
